import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Paperclip, Trash2, Download, RotateCcw, Clock, MessageSquare, History, PencilLine, AlertTriangle, Loader2 } from "lucide-react";
import { api, formatApiError, fileUrl } from "../../lib/api";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { TASK_STATUSES, PRIORITIES, TASK_STATUS_COLORS, PRIORITY_COLORS } from "./projectUtils";
import { assigneesOf, isOverdue, hoursDiff, todayISO } from "./taskUtils";
import AssigneePicker from "./AssigneePicker";
import { useConfirm } from "../ConfirmProvider";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

export default function TaskDetailDrawer({ taskId, open, onOpenChange, users, project, currentUser, canManage, onChanged }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const load = async () => {
    try { const { data } = await api.get(`/tasks/${taskId}`); setData(data); setLoadError(""); }
    catch (e) { setLoadError(formatApiError(e.response?.data?.detail) || "Could not load this task."); }
  };
  useEffect(() => { if (open && taskId) { setData(null); load(); } }, [open, taskId]);
  const refresh = async () => { await load(); onChanged?.(); };

  const t = data?.task;
  const canWork = canManage || (t && assigneesOf(t).includes(currentUser?.id));
  const props = { t, users, currentUser, canManage, canWork, refresh };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto bg-white p-0" data-testid="task-detail-drawer">
        <SheetTitle className="sr-only">Task details</SheetTitle>
        {loadError && <div className="p-6 text-sm text-rose-700" data-testid="task-detail-error">{loadError}</div>}
        {!t && !loadError && <div className="p-6 flex items-center gap-2 text-neutral-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading task…</div>}
        {t && (
          <div className="p-6 space-y-6">
            <Header {...props} onDuplicated={(id) => { onChanged?.(); toast.success("Task duplicated"); onOpenChange(false); }} />
            <Details {...props} project={project} />
            <Attachments {...props} />
            <WorkLogs {...props} />
            <Comments {...props} />
            <Activity items={data.activity} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Header({ t, canManage, canWork, refresh, onDuplicated }) {
  const setField = async (k, v) => { try { await api.put(`/tasks/${t.id}`, { [k]: v }); await refresh(); } catch (e) { err(e); } };
  const duplicate = async () => { try { const { data } = await api.post(`/tasks/${t.id}/duplicate`); onDuplicated(data.id); } catch (e) { err(e); } };
  return (
    <div className="space-y-3 pr-6">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-mono text-neutral-500" data-testid="td-task-id">{t.task_id}</span>
        {isOverdue(t) && <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-rose-50 border-rose-300 text-rose-700 flex items-center gap-1" data-testid="td-overdue"><AlertTriangle className="w-3 h-3" /> Overdue</span>}
        {t.duplicated_from && <span className="text-[10px] font-mono text-neutral-500">copy of {t.duplicated_from}</span>}
      </div>
      <h2 className="text-xl font-bold tracking-tight" data-testid="td-title">{t.name}</h2>
      <div className="flex items-center gap-2 flex-wrap">
        <select disabled={!canWork} value={t.status} onChange={e => setField("status", e.target.value)} data-testid="td-status"
          className={`text-xs font-mono rounded border px-2 py-1 ${TASK_STATUS_COLORS[t.status] || ""}`}>
          {TASK_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select disabled={!canManage} value={t.priority} onChange={e => setField("priority", e.target.value)} data-testid="td-priority"
          className={`text-xs font-mono rounded border px-2 py-1 ${PRIORITY_COLORS[t.priority] || ""}`}>
          {PRIORITIES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {t.status === "Completed" && canWork && <Button size="sm" variant="outline" onClick={() => setField("status", "To Do")} data-testid="td-reopen"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen</Button>}
        {canManage && <Button size="sm" variant="outline" onClick={duplicate} data-testid="td-duplicate"><Copy className="w-3.5 h-3.5 mr-1" /> Duplicate</Button>}
      </div>
    </div>
  );
}

function Details({ t, users, project, canManage, refresh }) {
  const pick = (x) => ({ name: x.name, description: x.description || "", assignees: assigneesOf(x), start_date: x.start_date || "", due_date: x.due_date || "", estimated_hours: x.estimated_hours || 0 });
  const [form, setForm] = useState(pick(t));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(pick(t)); }, [t]);
  const nameOf = (id) => users.find(u => u.id === id)?.name || "Unknown";
  const team = users.filter(u => ["developer", "tester"].includes(u.role) || u.id === project?.project_manager_id || (project?.development_team || []).includes(u.id) || form.assignees.includes(u.id));
  const diff = hoursDiff(t);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Title is required"); return; }
    if (form.start_date && form.due_date && form.due_date < form.start_date) { toast.error("Due date cannot be before start date"); return; }
    setBusy(true);
    try { await api.put(`/tasks/${t.id}`, { ...form, estimated_hours: Number(form.estimated_hours) || 0 }); toast.success("Task updated"); await refresh(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };

  return (
    <section className="space-y-3" data-testid="td-details">
      <div className="grid grid-cols-2 gap-3 text-xs">
        <Info label="Created by" value={`${t.created_by_name || nameOf(t.created_by)} · ${when(t.created_at)}`} />
        <Info label="Last updated" value={when(t.updated_at || t.created_at)} />
        <Info label="Hours (actual / est.)" value={<span data-testid="td-hours">{t.actual_hours || 0}h / {t.estimated_hours || 0}h · <span className={diff > 0 ? "text-rose-600" : "text-emerald-700"} data-testid="td-hours-diff">{diff > 0 ? `${diff}h over` : diff < 0 ? `${-diff}h left` : "on estimate"}</span></span>} />
        {!canManage && <Info label="Assignees" value={assigneesOf(t).map(nameOf).join(", ") || "Unassigned"} />}
      </div>
      {canManage ? (
        <div className="grid grid-cols-2 gap-3">
          <L label="Title" span><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="td-edit-name" /></L>
          <L label="Description" span><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="td-edit-desc" /></L>
          <L label={`Assignees (${form.assignees.length})`} span><AssigneePicker users={team} value={form.assignees} onChange={(a) => setForm({ ...form, assignees: a })} testid="td-assignees" /></L>
          <L label="Start date"><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="td-edit-start" /></L>
          <L label="Due date"><Input type="date" min={form.start_date || undefined} value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="td-edit-due" /></L>
          <L label="Estimated hours"><Input type="number" min="0" step="any" value={form.estimated_hours} onChange={e => setForm({ ...form, estimated_hours: e.target.value })} data-testid="td-edit-est" /></L>
          <div className="flex items-end justify-end"><Button onClick={save} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="td-save">{busy ? "Saving…" : "Save changes"}</Button></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 text-xs"><Info label="Start" value={t.start_date || "—"} /><Info label="Due" value={t.due_date || "—"} /></div>
          {t.description && <p className="text-sm text-neutral-700 whitespace-pre-wrap">{t.description}</p>}
        </>
      )}
    </section>
  );
}

function Attachments({ t, currentUser, canManage, canWork, refresh }) {
  const ref = useRef(null);
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/tasks/${t.id}/attachments`, { path: data.path, filename: data.filename, content_type: f.type });
      toast.success("Attachment added"); await refresh();
    } catch (e2) { err(e2); } finally { setBusy(false); }
  };
  const remove = async (a) => {
    if (!(await confirm({ title: `Remove "${a.filename}"?`, description: "The file will no longer be available on this task." }))) return;
    try { await api.delete(`/tasks/${t.id}/attachments`, { params: { path: a.path } }); await refresh(); } catch (e) { err(e); }
  };
  const atts = t.attachments || [];
  return (
    <Section icon={<Paperclip className="w-4 h-4" />} title={`Attachments (${atts.length})`} action={canWork && (
      <><input ref={ref} type="file" hidden onChange={onFile} data-testid="td-att-input" />
        <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid="td-att-add">{busy ? "Uploading…" : "Add file"}</Button></>
    )}>
      {atts.length === 0 && <Empty text="No attachments yet." />}
      {atts.map(a => (
        <div key={a.path} className="flex items-center gap-2 text-sm py-1.5" data-testid="td-att-item">
          <span className="truncate flex-1">{a.filename}</span>
          {a.uploaded_by_name && <span className="text-[10px] font-mono text-neutral-500">{a.uploaded_by_name}</span>}
          <a href={fileUrl(a.path)} target="_blank" rel="noreferrer" className="p-1 text-orange-700" data-testid="td-att-download"><Download className="w-4 h-4" /></a>
          {(canManage || a.uploaded_by === currentUser?.id) && <button onClick={() => remove(a)} className="p-1 text-rose-500" data-testid="td-att-remove"><Trash2 className="w-4 h-4" /></button>}
        </div>
      ))}
    </Section>
  );
}

function WorkLogs({ t, currentUser, canManage, canWork, refresh }) {
  const [form, setForm] = useState({ hours: "", date: todayISO(), note: "" });
  const add = async () => {
    if (!(Number(form.hours) > 0)) { toast.error("Enter hours greater than 0"); return; }
    try { await api.post(`/tasks/${t.id}/worklogs`, { ...form, hours: Number(form.hours) }); setForm({ hours: "", date: todayISO(), note: "" }); await refresh(); } catch (e) { err(e); }
  };
  const remove = async (w) => { try { await api.delete(`/tasks/${t.id}/worklogs/${w.id}`); await refresh(); } catch (e) { err(e); } };
  const logs = t.worklogs || [];
  return (
    <Section icon={<Clock className="w-4 h-4" />} title={`Work log (${logs.length})`}>
      {canWork && (
        <div className="flex gap-2 flex-wrap" data-testid="td-worklog-form">
          <Input type="number" min="0.1" step="0.25" placeholder="Hours" className="w-24" value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })} data-testid="td-worklog-hours" />
          <Input type="date" className="w-40" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="td-worklog-date" />
          <Input placeholder="What did you work on?" className="flex-1 min-w-[160px]" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} data-testid="td-worklog-note" />
          <Button size="sm" onClick={add} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="td-worklog-add">Log time</Button>
        </div>
      )}
      {logs.length === 0 && <Empty text="No time logged yet." />}
      {[...logs].reverse().map(w => (
        <div key={w.id} className="flex items-start gap-2 text-sm py-1.5" data-testid="td-worklog-item">
          <span className="font-semibold w-12">{w.hours}h</span>
          <div className="flex-1 min-w-0"><div className="text-[11px] font-mono text-neutral-500">{w.date} · {w.user_name}</div>{w.note && <div className="text-neutral-700">{w.note}</div>}</div>
          {(canManage || w.user_id === currentUser?.id) && <button onClick={() => remove(w)} className="p-1 text-rose-500" data-testid="td-worklog-remove"><Trash2 className="w-4 h-4" /></button>}
        </div>
      ))}
    </Section>
  );
}

function Comments({ t, currentUser, canManage, refresh }) {
  const confirm = useConfirm();
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(null);
  const add = async () => {
    if (!text.trim()) return;
    try { await api.post(`/tasks/${t.id}/comments`, { body: text }); setText(""); await refresh(); } catch (e) { err(e); }
  };
  const saveEdit = async () => {
    try { await api.put(`/tasks/${t.id}/comments/${editing.id}`, { body: editing.body }); setEditing(null); await refresh(); } catch (e) { err(e); }
  };
  const remove = async (c) => {
    if (!(await confirm({ title: "Delete this comment?", description: "This cannot be undone." }))) return;
    try { await api.delete(`/tasks/${t.id}/comments/${c.id}`); await refresh(); } catch (e) { err(e); }
  };
  const list = t.comments || [];
  return (
    <Section icon={<MessageSquare className="w-4 h-4" />} title={`Comments (${list.length})`}>
      <div className="space-y-2">
        <Textarea rows={2} placeholder="Write a comment… use @Name to mention someone" value={text} onChange={e => setText(e.target.value)} data-testid="td-comment-input" />
        <div className="flex justify-end"><Button size="sm" onClick={add} disabled={!text.trim()} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="td-comment-add">Comment</Button></div>
      </div>
      {list.length === 0 && <Empty text="No comments yet." />}
      {[...list].reverse().map(c => (
        <div key={c.id} className="py-2 border-t border-[#E4E7EC]" data-testid="td-comment-item">
          <div className="flex items-center gap-2 text-[11px] font-mono text-neutral-500">
            <span className="font-semibold text-neutral-800">{c.author_name}</span><span>{when(c.created_at)}</span>{c.edited_at && <span>(edited)</span>}
            <span className="ml-auto flex gap-1">
              {c.author_id === currentUser?.id && <button onClick={() => setEditing({ id: c.id, body: c.body })} className="p-1" data-testid="td-comment-edit"><PencilLine className="w-3.5 h-3.5" /></button>}
              {(c.author_id === currentUser?.id || canManage) && <button onClick={() => remove(c)} className="p-1 text-rose-500" data-testid="td-comment-delete"><Trash2 className="w-3.5 h-3.5" /></button>}
            </span>
          </div>
          {editing?.id === c.id ? (
            <div className="space-y-2 mt-1">
              <Textarea rows={2} value={editing.body} onChange={e => setEditing({ ...editing, body: e.target.value })} data-testid="td-comment-edit-input" />
              <div className="flex gap-2 justify-end"><Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button><Button size="sm" onClick={saveEdit} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="td-comment-edit-save">Save</Button></div>
            </div>
          ) : <div className="text-sm text-neutral-800 whitespace-pre-wrap mt-0.5">{c.body}</div>}
        </div>
      ))}
    </Section>
  );
}

function Activity({ items }) {
  return (
    <Section icon={<History className="w-4 h-4" />} title="Activity history">
      {items.length === 0 && <Empty text="No activity recorded yet." />}
      {items.map(a => (
        <div key={a.id} className="text-xs py-1.5 border-t border-[#E4E7EC]" data-testid="td-activity-item">
          <span className="font-semibold">{a.user_name}</span> {a.action}
          {(a.old || a.new) && <span className="text-neutral-600">: {a.old && <span className="line-through mr-1">{a.old}</span>}{a.new && <span className="text-neutral-900">{a.new}</span>}</span>}
          <div className="text-[10px] font-mono text-neutral-500">{when(a.created_at)}</div>
        </div>
      ))}
    </Section>
  );
}

const Section = ({ icon, title, action, children }) => (
  <section className="space-y-2">
    <div className="flex items-center justify-between"><h3 className="font-semibold text-sm flex items-center gap-2 text-neutral-800">{icon}{title}</h3>{action}</div>
    {children}
  </section>
);
const Info = ({ label, value }) => (<div><div className="text-[10px] font-mono uppercase text-neutral-500">{label}</div><div className="text-neutral-800">{value}</div></div>);
const L = ({ label, span, children }) => (<div className={`space-y-1 ${span ? "col-span-2" : ""}`}><div className="text-[10px] font-mono uppercase text-neutral-500">{label}</div>{children}</div>);
const Empty = ({ text }) => <div className="text-xs text-neutral-500">{text}</div>;
